import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, Minus, Plus, ShoppingCart, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, fmtTL } from "../lib/api";
import { fmtQty4 } from "../lib/format";
import QualityBadge from "../components/QualityBadge";
import { useShop } from "../context/ShopContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";

export default function Cart() {
    const navigate = useNavigate();
    const { refreshCart } = useShop();
    const [cart, setCart] = useState(null);
    const [rfqOpen, setRfqOpen] = useState(false);
    const [rfqNote, setRfqNote] = useState("");
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/cart").then((r) => setCart(r.data));
    useEffect(() => { load().catch(() => {}); }, []);

    const setQty = async (offerId, qty) => {
        if (qty < 1) return;
        const r = await api.patch(`/cart/items/${offerId}`, { qty });
        setCart(r.data);
        refreshCart();
    };

    const remove = async (offerId) => {
        const r = await api.delete(`/cart/items/${offerId}`);
        setCart(r.data);
        refreshCart();
        toast.success("Ürün sepetten çıkarıldı");
    };

    const checkout = async () => {
        setBusy(true);
        try {
            const r = await api.post("/cart/checkout", {});
            toast.success(`${r.data.orders.length} tedarikçiye sipariş iletildi`);
            await refreshCart();
            navigate("/panel/siparisler");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Sipariş oluşturulamadı");
        } finally {
            setBusy(false);
        }
    };

    const createRfq = async () => {
        setBusy(true);
        try {
            const items = [];
            for (const g of cart.groups) for (const it of g.items) items.push({ product_id: it.product_id, qty: it.qty });
            await api.post("/rfqs", { items, note: rfqNote, validity_days: 7 });
            toast.success("Teklif talebiniz tedarikçilere iletildi");
            setRfqOpen(false);
            navigate("/panel/teklifler");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Talep oluşturulamadı");
        } finally {
            setBusy(false);
        }
    };

    if (!cart) return <div className="h-96 animate-pulse rounded-2xl bg-muted" />;

    const grand = cart.groups.reduce((a, g) => a + g.subtotal, 0);

    return (
        <div className="space-y-6" data-testid="cart-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Sepet</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Sepetiniz tedarikçi bazında otomatik bölünür — her tedarikçi kendi siparişiyle ilerler.</p>
                </div>
                {cart.groups.length > 0 && (
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setRfqOpen(true)} data-testid="cart-create-rfq">
                            <FileText className="mr-2 h-4 w-4" /> Tümüne RFQ Gönder
                        </Button>
                        <Button onClick={checkout} disabled={busy} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="cart-checkout-button">
                            <ShoppingCart className="mr-2 h-4 w-4" /> Siparişi Tamamla — {fmtTL(grand * 1.1)}
                        </Button>
                    </div>
                )}
            </div>

            {!cart.groups.length && (
                <div className="rounded-2xl border border-dashed p-16 text-center" data-testid="cart-empty">
                    <ShoppingCart className="mx-auto h-10 w-10 text-muted-foreground" />
                    <p className="mt-4 text-sm text-muted-foreground">Sepetiniz boş.</p>
                    <Button asChild className="mt-4 bg-terra-600 hover:bg-terra-700 text-white"><Link to="/panel/katalog" data-testid="cart-go-catalog">Kataloğa Git</Link></Button>
                </div>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                <div className="space-y-6 lg:col-span-8">
                    {cart.groups.map((g) => (
                        <div key={g.supplier_id} className="overflow-hidden rounded-2xl border bg-card shadow-sm" data-testid={`cart-group-${g.supplier_id}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/50 px-5 py-3">
                                <div>
                                    <Link to={`/panel/vitrin/${g.supplier_id}`} className="font-display font-bold hover:text-primary">{g.supplier_name}</Link>
                                    <p className="text-xs text-muted-foreground">{g.supplier_city} • Güven {g.trust_score} • <Truck className="inline h-3 w-3" /> {g.max_delivery_days} gün</p>
                                </div>
                                <div className="w-48">
                                    <div className="flex justify-between text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                        <span>Min. sipariş</span>
                                        <span>{fmtTL(g.subtotal)} / {fmtTL(g.min_order)}</span>
                                    </div>
                                    <Progress value={Math.min(100, (g.subtotal / Math.max(g.min_order, 1)) * 100)} className="mt-1 h-1.5" data-testid={`min-order-progress-${g.supplier_id}`} />
                                    {g.subtotal < g.min_order && <p className="mt-1 text-[10px] font-semibold text-amber-700">Kargo barajı için {fmtTL(g.min_order - g.subtotal)} kaldı</p>}
                                </div>
                            </div>
                            {g.items.map((it) => (
                                <div key={it.offer_id} className="flex items-center gap-4 border-b px-5 py-4 last:border-0" data-testid={`cart-item-${it.offer_id}`}>
                                    <img src={it.image} alt={it.product_name} className="h-14 w-14 rounded-xl object-cover" />
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-semibold">{it.product_name} <QualityBadge quality={it.quality} tid={it.offer_id} /></p>
                                        <p className="text-xs text-muted-foreground">{fmtTL(it.unit_price)} / {it.unit} • stok {fmtQty4(it.stock, it.unit)}</p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setQty(it.offer_id, Math.max(it.moq, it.qty - 1))} data-testid={`qty-minus-${it.offer_id}`}><Minus className="h-3 w-3" /></Button>
                                        <span className="w-14 text-center text-sm font-semibold" data-testid={`qty-value-${it.offer_id}`}>{fmtQty4(it.qty, it.unit)} {it.unit}</span>
                                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setQty(it.offer_id, it.qty + 1)} data-testid={`qty-plus-${it.offer_id}`}><Plus className="h-3 w-3" /></Button>
                                    </div>
                                    <p className="w-28 text-right font-display font-extrabold text-terra-600">{fmtTL(it.line_total)}</p>
                                    <Button variant="ghost" size="icon" onClick={() => remove(it.offer_id)} data-testid={`cart-remove-${it.offer_id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                {cart.groups.length > 0 && (
                    <div className="lg:col-span-4">
                        <div className="sticky top-24 rounded-2xl border bg-card p-6 shadow-sm" data-testid="cart-summary">
                            <h2 className="font-display text-lg font-bold">Sipariş Özeti</h2>
                            <div className="mt-4 space-y-2 text-sm">
                                {cart.groups.map((g) => (
                                    <div key={g.supplier_id} className="flex justify-between text-muted-foreground">
                                        <span className="truncate pr-2">{g.supplier_name}</span>
                                        <span className="font-medium text-foreground">{fmtTL(g.subtotal)}</span>
                                    </div>
                                ))}
                                <div className="flex justify-between border-t pt-2 text-muted-foreground">
                                    <span>Ara Toplam</span><span className="font-medium text-foreground">{fmtTL(grand)}</span>
                                </div>
                                <div className="flex justify-between text-muted-foreground">
                                    <span>KDV (%10)</span><span className="font-medium text-foreground">{fmtTL(grand * 0.1)}</span>
                                </div>
                                <div className="flex justify-between border-t pt-2">
                                    <span className="font-display font-bold">Genel Toplam</span>
                                    <span className="font-display text-lg font-extrabold text-terra-600" data-testid="cart-grand-total">{fmtTL(grand * 1.1)}</span>
                                </div>
                            </div>
                            <p className="mt-3 rounded-lg bg-muted p-3 text-xs text-muted-foreground">
                                {cart.groups.length} tedarikçiye bölünmüş {cart.groups.length} ayrı sipariş oluşturulacak.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={rfqOpen} onOpenChange={setRfqOpen}>
                <DialogContent data-testid="rfq-dialog">
                    <DialogHeader><DialogTitle className="font-display">Toplu Teklif Talebi (RFQ)</DialogTitle></DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        Sepetinizdeki {cart.groups.reduce((a, g) => a + g.items.length, 0)} kalem ürün için tüm uygun tedarikçilere teklif talebi gönderilecek.
                    </p>
                    <Textarea value={rfqNote} onChange={(e) => setRfqNote(e.target.value)} placeholder="Not: teslim tarihi, kalite beklentisi vb." data-testid="rfq-note-input" />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRfqOpen(false)}>Vazgeç</Button>
                        <Button onClick={createRfq} disabled={busy} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="rfq-submit">RFQ Gönder</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
